/**
 * Right-click Copy / Paste / Clear for an editable board cell (monday.com
 * style). Wraps the cell's content as a `ContextMenu` trigger; left-click still
 * reaches the cell's own editor, only right-click opens this menu.
 *
 * - **Copy** stores `{ type, value }` in the typed in-app clipboard
 *   ({@link useCellClipboardStore}) — never the OS clipboard.
 * - **Paste** is enabled only when the clipboard holds the *same* column type,
 *   and writes the raw stored value via `onChange`. Note: a `status` value
 *   pasted into a *different* status column carries the option **id**; if the
 *   target column lacks it the cell shows empty (same-type contract is still
 *   honored). A label-based remap can come later.
 * - **Clear** writes `null` (the shared cell contract already treats that as
 *   "no value"); disabled when the cell is already empty, and absent on a
 *   column the record cannot hold empty, where the write would be refused.
 *
 * Only mounted for the editable custom types (see `isCopyableType`).
 */
import type { ReactNode } from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@monksflow/monks-ui';
import { ClipboardPaste, Copy, Eraser } from 'lucide-react';

import { track } from '@/shared/analytics';

import type { CustomColumnType } from '../../lib/useTrackersBoard';
import { useCellClipboardStore } from '../../lib/useCellClipboard';
import { useTranslation } from 'react-i18next';

interface CellContextMenuProps {
  columnType: CustomColumnType;
  value: unknown;
  /** Whether the cell currently holds a clearable value (drives Clear). */
  hasValue: boolean;
  clearable?: boolean;
  boardType: 'briefs' | 'free_form';
  /** Persist a new value (or `null` to clear) for this cell. */
  onChange: (next: unknown) => void;
  children: ReactNode;
}

export function CellContextMenu({
  columnType,
  value,
  hasValue,
  clearable = true,
  boardType,
  onChange,
  children,
}: CellContextMenuProps) {
  const { t } = useTranslation('trackers');

  // Narrow selector: only re-renders when *this* type's paste-ability flips.
  const canPaste = useCellClipboardStore((s) => s.clipboard?.type === columnType);
  const copy = useCellClipboardStore((s) => s.copy);

  const handleCopy = () => {
    copy(columnType, value);
    track('tracker_cell_copied', { board_type: boardType, column_type: columnType });
  };

  const handlePaste = () => {
    const entry = useCellClipboardStore.getState().clipboard;
    if (!entry || entry.type !== columnType) return;
    onChange(entry.value);
    track('tracker_cell_pasted', { board_type: boardType, column_type: columnType });
  };

  const handleClear = () => {
    onChange(null);
    track('tracker_cell_cleared', { board_type: boardType, column_type: columnType });
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div className="flex items-center justify-center overflow-hidden px-2 py-1.5">
          {children}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-40">
        <ContextMenuItem onSelect={handleCopy}>
          <Copy aria-hidden />
          {t('trackersBoard.cellContextMenu.copy')}
        </ContextMenuItem>
        <ContextMenuItem onSelect={handlePaste} disabled={!canPaste}>
          <ClipboardPaste aria-hidden />
          {t('trackersBoard.cellContextMenu.paste')}
        </ContextMenuItem>
        {clearable ? (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onSelect={handleClear} disabled={!hasValue}>
              <Eraser aria-hidden />
              {t('trackersBoard.cellContextMenu.clear')}
            </ContextMenuItem>
          </>
        ) : null}
      </ContextMenuContent>
    </ContextMenu>
  );
}
